// =============================================================================
// LP Express — Listicle template renderer.
//
// Renders a `generatedContent.listicle` blob (see shared/lp-express-listicle.ts)
// as a long-scroll persuasion page: announcement marquee → hero + offer card →
// 10 numbered reasons (each with an optional typed supporting visual)
// interleaved with a membership-framing block and three CTA blocks → science →
// trust montage → membership explainer → roadmap → FAQ → final CTA → footer →
// sticky mobile CTA bar.
//
//  • Copy is fully editable in the builder via the path-based `setText` ops the
//    other templates use (paths like ["listicle","reasons",0,"headline"]).
//  • AI imagery comes from `generatedContent.listicleImages` per-slot records
//    (pending/ready/failed), with an in-builder retry on failed slots.
//  • Product cards, order summaries, prices, discounts and reviews are ALWAYS
//    derived from the real brand/product/offer/review data — the generated
//    copy never carries dollar amounts.
//  • Brand adaptation comes from the shared --color-brand-* CSS variables the
//    renderer already sets (brandCssVarsV2), so the same layout re-skins per
//    brand with readable contrast.
// =============================================================================
import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2, ChevronDown, XCircle, Star, Loader2, RotateCw, Sparkles, Flower2, Leaf,
} from "lucide-react";
import {
  EditableText,
  EditableElement,
  LpReorderGroup,
  useLpEditor,
  LpReviewActions,
  useLpReviewOverride,
  type LpReviewChoice,
} from "@/components/lp-editor";
import type { Product } from "@shared/schema";
import type { LpReceipt, LpReviewDisplay } from "@/lib/lp-express-pricing";
import {
  normalizeLpListicleContent,
  type LpListicleContent,
  type LpListicleReason,
} from "@shared/lp-express-listicle";

export type ListicleProductCard = {
  name: string;
  priceLabel: string | null;
  imageUrl: string | null;
};

export type ListicleCtx = {
  brand: any;
  offer: { checkoutUrl?: string } | any;
  generatedContent: any;
  logoUrl: string | null;
  bundleProductPhotos: string[];
  bundleImage?: string | null;
  products?: Product[];
  /** Real product cards (photo + name + price) derived by the renderer. */
  productCards?: ListicleProductCard[];
  receipt?: LpReceipt;
  reviewDisplay?: LpReviewDisplay;
  typography?: { heading?: string; body?: string } | any;
  /** In-builder retry for a failed AI image slot; absent on the public page. */
  onRetrySlot?: (mergeKey: "listicleImages", slotKey: string) => void;
};

// Shared brand palette (same CSS vars the quiz/long-form templates use, set by
// the renderer root via brandCssVarsV2 → adapts per brand automatically).
const HEADING = "var(--color-brand-navy)";
const ACCENT = "var(--color-brand-green)";
const DEEP = "var(--color-brand-deepnavy)";
const CREAM = "var(--color-brand-cream)";
const TEXT = "var(--color-brand-text)";
const MUTED = "var(--color-brand-muted)";
const CARD = "var(--color-brand-card)";
const BORDER = "var(--color-brand-lightborder)";
const GOLD = "var(--color-brand-gold)";
const CORAL = "var(--color-brand-coral)";
const PAGE_BG = "rgb(250, 250, 247)";

// ── Image slot helpers ───────────────────────────────────────────────────────

type SlotRecord = { key: string; imageUrl?: string | null; imageStatus?: string; altText?: string };

function useListicleSlots(gc: any): Record<string, SlotRecord> {
  const arr: SlotRecord[] = Array.isArray(gc?.listicleImages) ? gc.listicleImages : [];
  const map: Record<string, SlotRecord> = {};
  for (const s of arr) if (s?.key) map[s.key] = s;
  return map;
}

// One AI image slot: ready → the image; pending → spinner; failed → subdued
// placeholder with an in-builder retry (public page shows nothing clickable).
function ImgSlot({
  slot,
  slotKey,
  className,
  style,
  onRetry,
  testId,
}: {
  slot: SlotRecord | undefined;
  slotKey: string;
  className?: string;
  style?: React.CSSProperties;
  onRetry?: (key: string) => void;
  testId: string;
}) {
  const status = slot?.imageStatus;
  if (slot?.imageUrl && status !== "failed") {
    return (
      <img
        src={slot.imageUrl}
        alt={slot.altText || ""}
        loading="lazy"
        className={className}
        style={{ objectFit: "cover", display: "block", width: "100%", height: "100%", ...style }}
        data-testid={testId}
      />
    );
  }
  const failed = status === "failed";
  return (
    <div
      className={className}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 6, width: "100%", height: "100%", minHeight: 90, backgroundColor: "#F1EDE4", ...style,
      }}
      data-testid={testId}
    >
      {failed ? (
        onRetry ? (
          <button
            type="button"
            onClick={() => onRetry(slotKey)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold"
            style={{ color: HEADING, border: `1px solid ${BORDER}`, backgroundColor: "#fff" }}
            data-testid={`button-retry-${testId}`}
          >
            <RotateCw className="h-3 w-3" /> Retry image
          </button>
        ) : (
          <Sparkles className="h-4 w-4" style={{ color: MUTED }} />
        )
      ) : (
        <Loader2 className="h-4 w-4 animate-spin" style={{ color: MUTED }} />
      )}
    </div>
  );
}

// ── Small shared pieces ──────────────────────────────────────────────────────

function TrustBullets({ items }: { items: string[] }) {
  const editor = useLpEditor();
  if (!items.length && !editor) return null;
  const shown = editor && !items.length ? ["", ""] : items;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", justifyContent: "center" }}>
      {shown.map((t, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <CheckCircle2 style={{ width: 10, height: 10, color: ACCENT, flexShrink: 0 }} />
          <EditableText
            value={t}
            path={["listicle", "trustBullets", i]}
            as="span"
            style={{ fontSize: 9, fontWeight: 600, color: MUTED }}
            placeholder="Trust point"
            testId={`text-listicle-trust-bullet-${i}`}
          />
        </div>
      ))}
    </div>
  );
}

function CtaButton({
  label,
  path,
  href,
  testId,
}: {
  label: string;
  path: (string | number)[];
  href?: string;
  testId: string;
}) {
  const editor = useLpEditor();
  const inner = (
    <EditableText
      value={label}
      path={path}
      as="span"
      noHideDelete
      placeholder="Claim the offer"
      testId={`${testId}-label`}
    />
  );
  const style: React.CSSProperties = {
    display: "block", width: "100%", padding: "16px 0", backgroundColor: ACCENT, color: "#FFFFFF",
    fontSize: 17, fontWeight: 800, textAlign: "center", borderRadius: 12, letterSpacing: "0.02em",
    textDecoration: "none", boxShadow: "0 4px 14px rgba(0,0,0,0.18)", cursor: "pointer",
  };
  if (editor || !href) {
    return (
      <span style={{ ...style, ...(editor ? {} : { opacity: 0.7, cursor: "not-allowed" }) }} data-testid={testId} data-lp-cta>
        {inner}
      </span>
    );
  }
  return (
    <a href={href} style={style} data-testid={testId} data-lp-cta>
      {inner}
    </a>
  );
}

function SectionNumber({ n }: { n: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: HEADING, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 900, color: "#FFFFFF" }}>{n}</span>
      </div>
      <div style={{ flex: 1, height: 1, backgroundColor: BORDER }} />
    </div>
  );
}

function Eyebrow({ value, path, testId, color = "#9A8E80" }: { value: string; path: (string | number)[]; testId: string; color?: string }) {
  return (
    <EditableText
      value={value}
      path={path}
      as="p"
      className="uppercase"
      style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.22em", color, textAlign: "center", marginBottom: 6 }}
      placeholder="Eyebrow"
      testId={testId}
    />
  );
}

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const editor = useLpEditor();
  // Every question starts COLLAPSED (public page and editor alike). Public
  // page: the whole row toggles. Editor: only the chevron toggles, so
  // clicking the question text still enters inline editing.
  const [open, setOpen] = useState(false);
  return (
    <div style={{ backgroundColor: CARD, borderRadius: 10, padding: "12px 16px", border: `1px solid ${BORDER}` }} data-testid={`listicle-faq-item-${index}`}>
      <button
        type="button"
        onClick={editor ? undefined : () => setOpen(!open)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", textAlign: "left", background: "none", border: "none", cursor: editor ? "default" : "pointer", gap: 12, padding: 0 }}
        data-testid={`button-listicle-faq-toggle-${index}`}
      >
        <EditableText
          value={q}
          path={["listicle", "faq", "items", index, "q"]}
          as="span"
          style={{ fontSize: 14, fontWeight: 700, color: HEADING, lineHeight: 1.35 }}
          placeholder="Question"
          testId={`text-listicle-faq-q-${index}`}
        />
        <ChevronDown
          size={18}
          onClick={editor ? (e) => { e.stopPropagation(); setOpen(!open); } : undefined}
          style={{ color: HEADING, flexShrink: 0, transform: open ? "rotate(180deg)" : undefined, transition: "transform 0.2s", cursor: "pointer" }}
        />
      </button>
      {open && (
        <EditableText
          value={a}
          path={["listicle", "faq", "items", index, "a"]}
          as="div"
          multiline
          style={{ paddingTop: 10, fontSize: 13, color: MUTED, lineHeight: 1.6 }}
          placeholder="Answer"
          testId={`text-listicle-faq-a-${index}`}
        />
      )}
    </div>
  );
}

// ── Derived visuals ──────────────────────────────────────────────────────────

function ProductGridVisual({ ctx }: { ctx: ListicleCtx }) {
  const cards = (ctx.productCards ?? []).slice(0, 4);
  if (!cards.length) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {cards.map((p, i) => (
        <div key={i} style={{ backgroundColor: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, overflow: "hidden" }} data-testid={`listicle-product-card-${i}`}>
          {p.imageUrl && (
            <div style={{ height: 120, overflow: "hidden" }}>
              <img src={p.imageUrl} alt={p.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
          )}
          <div style={{ padding: "8px 10px" }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: HEADING, margin: 0 }}>{p.name}</p>
            {p.priceLabel && <p style={{ fontSize: 10, color: MUTED, margin: "2px 0 0" }}>{p.priceLabel}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ValueBreakdownVisual({ ctx }: { ctx: ListicleCtx }) {
  const receipt = ctx.receipt;
  if (!receipt || !receipt.lineItems.length) return null;
  return (
    <div style={{ backgroundColor: CARD, borderRadius: 12, border: `1.5px solid ${BORDER}`, overflow: "hidden" }} data-testid="listicle-value-breakdown">
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
        <p className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: MUTED, margin: 0 }}>Value Breakdown</p>
      </div>
      {receipt.lineItems.map((v, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${BORDER}` }}>
          <span style={{ fontSize: 13, color: TEXT }}>{v.name}</span>
          {v.priceLabel && <span style={{ fontSize: 13, fontWeight: 600, color: MUTED }}>{v.priceLabel}</span>}
        </div>
      ))}
      <div style={{ padding: "14px 16px", backgroundColor: CREAM }}>
        {receipt.hasDiscount && receipt.retailLabel && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#999" }}>Retail Total</span>
            <span style={{ fontSize: 14, color: "#999", textDecoration: "line-through" }}>{receipt.retailLabel}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 900, color: HEADING }}>You Pay Today</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {receipt.hasDiscount && receipt.discountPercent != null && (
              <span style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 10, padding: "2px 6px", borderRadius: 3 }}>
                {receipt.discountPercent}% OFF
              </span>
            )}
            <span style={{ fontSize: 22, fontWeight: 900, color: ACCENT }}>{receipt.priceTodayLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// A single listicle review slot. Defaults to the Nth aggregated real review,
// but in the editor exposes the same review picker the long-form hero and quiz
// use (LpReviewActions) so operators can feature any REAL review in this slot.
// The chosen review is snapshotted on generatedContent.reviewOverrides[slotKey]
// (snapshot, not id — durable through publish and review-list reordering).
function ListicleReviewSlot({
  slot,
  options,
}: {
  slot: number;
  options: LpReviewChoice[];
}) {
  const slotKey = `listicle:reviews:${slot}`;
  const override = useLpReviewOverride(slotKey);
  const rev = override ?? options[slot];
  const AVATARS = ["#E8927C", "#7BBFB2", "#A78BCA", "#E0A45E", "#8CB4D9", "#C4A265"];
  if (!rev) return null;
  return (
    <div className="group/lpreview relative" style={{ backgroundColor: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "12px 14px" }} data-testid={`listicle-review-${slot}`}>
      <LpReviewActions slotKey={slotKey} reviews={options} testIdPrefix={`listicle-review-${slot}`} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: AVATARS[slot % AVATARS.length], display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>{(rev.reviewer || "A")[0]}</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: HEADING }}>{rev.reviewer || "Verified customer"}</span>
        <span style={{ display: "inline-flex", color: "#F5A623" }}>
          {Array.from({ length: Math.max(1, Math.min(5, rev.rating)) }).map((_, s) => (
            <Star key={s} style={{ width: 10, height: 10 }} className="fill-current" />
          ))}
        </span>
        <span style={{ fontSize: 8, fontWeight: 700, color: ACCENT, backgroundColor: "rgba(45,122,79,0.08)", padding: "2px 5px", borderRadius: 3, marginLeft: "auto" }}>Verified</span>
      </div>
      <p style={{ fontSize: 12, lineHeight: 1.55, color: TEXT, margin: 0 }}>"{rev.reviewText}"</p>
    </div>
  );
}

function ReviewsVisual({ ctx }: { ctx: ListicleCtx }) {
  const options = (ctx.reviewDisplay?.allReviews ?? []) as LpReviewChoice[];
  if (!options.length) return null;
  const slotCount = Math.min(6, options.length);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {Array.from({ length: slotCount }).map((_, i) => (
        <ListicleReviewSlot key={i} slot={i} options={options} />
      ))}
    </div>
  );
}

// ── Reason visual dispatcher ─────────────────────────────────────────────────

function ReasonVisual({
  reason,
  reasonIndex,
  ctx,
  slots,
}: {
  reason: LpListicleReason;
  reasonIndex: number;
  ctx: ListicleCtx;
  slots: Record<string, SlotRecord>;
}) {
  const v = reason.visual;
  const base: (string | number)[] = ["listicle", "reasons", reasonIndex, "visual"];
  const retry = ctx.onRetrySlot ? (key: string) => ctx.onRetrySlot!("listicleImages", key) : undefined;
  switch (v.type) {
    case "productGrid":
      return <ProductGridVisual ctx={ctx} />;
    case "valueBreakdown":
      return <ValueBreakdownVisual ctx={ctx} />;
    case "reviews":
      return <ReviewsVisual ctx={ctx} />;
    case "benefitList":
      if (!v.items.length) return null;
      return (
        <div style={{ backgroundColor: CREAM, borderRadius: 12, padding: 18, border: `1px solid ${BORDER}` }}>
          {v.items.map((b, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: i < v.items.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <EditableText value={b.title} path={[...base, "items", i, "title"]} as="p" style={{ fontSize: 13, fontWeight: 800, color: HEADING, margin: "0 0 3px" }} placeholder="Benefit" testId={`text-listicle-r${reasonIndex}-benefit-title-${i}`} />
              <EditableText value={b.desc} path={[...base, "items", i, "desc"]} as="p" multiline style={{ fontSize: 12, color: MUTED, lineHeight: 1.55, margin: 0 }} placeholder="Why it matters" testId={`text-listicle-r${reasonIndex}-benefit-desc-${i}`} />
            </div>
          ))}
        </div>
      );
    case "imageCards":
      if (!v.items.length) return null;
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {v.items.map((ing, i) => (
            <div key={i} style={{ backgroundColor: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
              <div style={{ height: 100, overflow: "hidden" }}>
                <ImgSlot slot={slots[`science-${i + 1}`]} slotKey={`science-${i + 1}`} onRetry={retry} testId={`img-listicle-r${reasonIndex}-card-${i}`} />
              </div>
              <div style={{ padding: "8px 10px" }}>
                <EditableText value={ing.name} path={[...base, "items", i, "name"]} as="p" style={{ fontSize: 11, fontWeight: 700, color: HEADING, margin: 0 }} placeholder="Name" testId={`text-listicle-r${reasonIndex}-card-name-${i}`} />
                <EditableText value={ing.desc} path={[...base, "items", i, "desc"]} as="p" style={{ fontSize: 9, color: MUTED, margin: "2px 0 0", lineHeight: 1.4 }} placeholder="One-line detail" testId={`text-listicle-r${reasonIndex}-card-desc-${i}`} />
              </div>
            </div>
          ))}
        </div>
      );
    case "lifestylePair": {
      const items = v.items.length ? v.items : [{ label: "" }, { label: "" }];
      return (
        <div style={{ display: "flex", gap: 6 }}>
          {items.slice(0, 2).map((f, i) => (
            <div key={i} style={{ flex: 1, borderRadius: 10, overflow: "hidden", position: "relative" }}>
              <div style={{ height: 140 }}>
                <ImgSlot slot={slots[`lifestyle-${i + 1}`]} slotKey={`lifestyle-${i + 1}`} onRetry={retry} testId={`img-listicle-r${reasonIndex}-lifestyle-${i}`} />
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "6px 8px", background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)" }}>
                <EditableText value={f.label} path={[...base, "items", i, "label"]} as="span" className="uppercase" style={{ fontSize: 9, fontWeight: 700, color: "#FFFFFF", letterSpacing: "0.1em" }} placeholder="Caption" testId={`text-listicle-r${reasonIndex}-lifestyle-label-${i}`} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    case "gallery": {
      const items = v.items.length ? v.items : Array.from({ length: 4 }, () => ({ caption: "" }));
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {items.slice(0, 4).map((p, i) => (
            <div key={i} style={{ borderRadius: 8, overflow: "hidden", position: "relative" }}>
              <div style={{ height: 110 }}>
                <ImgSlot slot={slots[`gallery-${i + 1}`]} slotKey={`gallery-${i + 1}`} onRetry={retry} testId={`img-listicle-r${reasonIndex}-gallery-${i}`} />
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "4px 6px", background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent)" }}>
                <EditableText value={p.caption} path={[...base, "items", i, "caption"]} as="span" className="uppercase" style={{ fontSize: 8, fontWeight: 700, color: "#fff", letterSpacing: "0.08em" }} placeholder="Caption" testId={`text-listicle-r${reasonIndex}-gallery-cap-${i}`} />
              </div>
            </div>
          ))}
        </div>
      );
    }
    case "comparison":
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, borderRadius: 12, overflow: "hidden", border: `1.5px solid ${BORDER}` }}>
          <div style={{ backgroundColor: "#F5F3EF", padding: "14px 12px" }}>
            <EditableText value={v.leftLabel} path={[...base, "leftLabel"]} as="p" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.15em", color: "#999", marginBottom: 10 }} placeholder="Them" testId={`text-listicle-r${reasonIndex}-cmp-left-label`} />
            {v.leftItems.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <XCircle style={{ width: 12, height: 12, color: "#C0392B", flexShrink: 0 }} />
                <EditableText value={t} path={[...base, "leftItems", i]} as="span" style={{ fontSize: 11, color: MUTED }} placeholder="Their downside" testId={`text-listicle-r${reasonIndex}-cmp-left-${i}`} />
              </div>
            ))}
          </div>
          <div style={{ backgroundColor: CARD, padding: "14px 12px" }}>
            <EditableText value={v.rightLabel} path={[...base, "rightLabel"]} as="p" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.15em", color: ACCENT, marginBottom: 10 }} placeholder="Us" testId={`text-listicle-r${reasonIndex}-cmp-right-label`} />
            {v.rightItems.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <CheckCircle2 style={{ width: 12, height: 12, color: ACCENT, flexShrink: 0 }} />
                <EditableText value={t} path={[...base, "rightItems", i]} as="span" style={{ fontSize: 11, color: HEADING, fontWeight: 600 }} placeholder="Our upside" testId={`text-listicle-r${reasonIndex}-cmp-right-${i}`} />
              </div>
            ))}
          </div>
        </div>
      );
    case "infoTable":
      if (!v.rows.length) return null;
      return (
        <div style={{ backgroundColor: CREAM, borderRadius: 12, padding: 18, border: `1px solid ${BORDER}` }}>
          <EditableText value={v.title} path={[...base, "title"]} as="p" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: MUTED, marginBottom: 10 }} placeholder="Table title" testId={`text-listicle-r${reasonIndex}-table-title`} />
          {v.rows.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderBottom: i < v.rows.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <EditableText value={m.label} path={[...base, "rows", i, "label"]} as="span" style={{ fontSize: 12, fontWeight: 700, color: HEADING }} placeholder="Label" testId={`text-listicle-r${reasonIndex}-table-label-${i}`} />
              <EditableText value={m.value} path={[...base, "rows", i, "value"]} as="span" style={{ fontSize: 12, color: MUTED, textAlign: "right" }} placeholder="Value" testId={`text-listicle-r${reasonIndex}-table-value-${i}`} />
            </div>
          ))}
        </div>
      );
    case "manifesto":
      return (
        <div style={{ backgroundColor: DEEP, borderRadius: 12, padding: 18 }}>
          <EditableText value={v.isLabel} path={[...base, "isLabel"]} as="p" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: ACCENT, marginBottom: 10 }} placeholder="What it is" testId={`text-listicle-r${reasonIndex}-is-label`} />
          {v.isItems.map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <CheckCircle2 style={{ width: 12, height: 12, color: ACCENT, flexShrink: 0 }} />
              <EditableText value={t} path={[...base, "isItems", i]} as="span" style={{ fontSize: 12, color: "rgba(255,255,255,0.92)", fontWeight: 600 }} placeholder="What it is" testId={`text-listicle-r${reasonIndex}-is-${i}`} />
            </div>
          ))}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: 8, paddingTop: 12 }}>
            <EditableText value={v.isntLabel} path={[...base, "isntLabel"]} as="p" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: GOLD, marginBottom: 12 }} placeholder="And what it isn't" testId={`text-listicle-r${reasonIndex}-isnt-label`} />
            {v.isntItems.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <XCircle style={{ width: 12, height: 12, color: CORAL, flexShrink: 0 }} />
                <EditableText value={t} path={[...base, "isntItems", i]} as="span" style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }} placeholder="What it isn't" testId={`text-listicle-r${reasonIndex}-isnt-${i}`} />
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

// ── Interleaved blocks ───────────────────────────────────────────────────────

function MembershipFramingBlock({ listicle, ctx }: { listicle: LpListicleContent; ctx: ListicleCtx }) {
  const m = listicle.membership;
  const receipt = ctx.receipt;
  return (
    <EditableElement elementKey="listicle-membership" toolbarSide="left">
      <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${GOLD}`, overflow: "hidden", marginBottom: 32, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }} data-testid="listicle-membership">
        <div style={{ backgroundColor: HEADING, padding: "10px 16px", textAlign: "center" }}>
          <EditableText value={m.header} path={["listicle", "membership", "header"]} as="span" className="uppercase" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: GOLD }} placeholder="How the membership works" testId="text-listicle-membership-header" />
        </div>
        <div style={{ padding: "18px 18px 16px" }}>
          <EditableText value={m.headline} path={["listicle", "membership", "headline"]} as="h3" style={{ fontSize: 17, fontWeight: 900, color: HEADING, lineHeight: 1.3, margin: "0 0 8px" }} placeholder="Membership headline" testId="text-listicle-membership-headline" />
          <EditableText value={m.body} path={["listicle", "membership", "body"]} as="p" multiline style={{ fontSize: 13, lineHeight: 1.6, color: MUTED, margin: "0 0 14px" }} placeholder="Explain why the model keeps it fresh and affordable." testId="text-listicle-membership-body" />
          {(m.stats.length > 0 || receipt) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              {(m.stats.length ? m.stats : [{ value: "", label: "", sub: "" }, { value: "", label: "", sub: "" }]).slice(0, 2).map((b, i) => (
                <div key={i} style={{ backgroundColor: CREAM, borderRadius: 10, padding: "12px 10px", textAlign: "center", border: `1px solid ${BORDER}` }}>
                  <EditableText value={b.label} path={["listicle", "membership", "stats", i, "label"]} as="p" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.15em", color: MUTED, margin: 0 }} placeholder="Label" testId={`text-listicle-membership-stat-label-${i}`} />
                  {/* Prefer REAL derived prices: first stat = today's price, second = refill. */}
                  <p style={{ fontSize: 22, fontWeight: 900, color: ACCENT, margin: "4px 0 2px" }} data-testid={`text-listicle-membership-stat-value-${i}`}>
                    {i === 0 ? receipt?.priceTodayLabel ?? b.value : receipt?.refillLabel ?? b.value}
                  </p>
                  <EditableText value={b.sub} path={["listicle", "membership", "stats", i, "sub"]} as="p" style={{ fontSize: 10, color: MUTED, margin: 0 }} placeholder="Detail" testId={`text-listicle-membership-stat-sub-${i}`} />
                </div>
              ))}
            </div>
          )}
          {m.bullets.length > 0 && (
            <div style={{ backgroundColor: CREAM, borderRadius: 8, padding: "10px 12px" }}>
              {m.bullets.map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "3px 0" }}>
                  <CheckCircle2 style={{ width: 13, height: 13, color: ACCENT, flexShrink: 0, marginTop: 2 }} />
                  <EditableText value={t} path={["listicle", "membership", "bullets", i]} as="span" style={{ fontSize: 12, color: TEXT, lineHeight: 1.45 }} placeholder="Flexibility point" testId={`text-listicle-membership-bullet-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </EditableElement>
  );
}

function CtaSummaryBlock({ listicle, ctx }: { listicle: LpListicleContent; ctx: ListicleCtx }) {
  const c = listicle.ctaSummary;
  const receipt = ctx.receipt;
  return (
    <EditableElement elementKey="listicle-cta-summary" toolbarSide="left">
      <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, overflow: "hidden", marginBottom: 40, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }} data-testid="listicle-cta-summary">
        {ctx.bundleImage && <img src={ctx.bundleImage} alt="" loading="lazy" style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover" }} />}
        <div style={{ backgroundColor: HEADING, padding: "10px 16px", textAlign: "center" }}>
          <EditableText value={c.header} path={["listicle", "ctaSummary", "header"]} as="span" className="uppercase" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: "#FFFFFF" }} placeholder="Your order summary" testId="text-listicle-cta-summary-header" />
        </div>
        <div style={{ padding: 16 }}>
          {(receipt?.lineItems ?? []).map((item, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 13, color: TEXT }}>{item.name}</span>
              {item.priceLabel && (
                <span style={{ fontSize: 13, color: "#999", textDecoration: receipt?.hasDiscount ? "line-through" : "none" }}>{item.priceLabel}</span>
              )}
            </div>
          ))}
          {receipt && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 4px", borderTop: `2px solid ${HEADING}`, marginTop: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: HEADING }}>Today's Price</span>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: 20, fontWeight: 900, color: ACCENT }}>{receipt.priceTodayLabel}</span>
                {receipt.hasDiscount && receipt.retailLabel && (
                  <span style={{ fontSize: 11, color: "#999", marginLeft: 6, textDecoration: "line-through" }}>{receipt.retailLabel}</span>
                )}
              </div>
            </div>
          )}
          <EditableText value={c.savingsNote} path={["listicle", "ctaSummary", "savingsNote"]} as="p" style={{ fontSize: 11, color: ACCENT, fontWeight: 600, textAlign: "center", margin: "6px 0 0" }} placeholder="Qualitative savings line" testId="text-listicle-cta-summary-note" />
          <div style={{ marginTop: 12 }}>
            <CtaButton label={c.ctaText} path={["listicle", "ctaSummary", "ctaText"]} href={ctx.offer?.checkoutUrl} testId="button-listicle-cta-summary" />
          </div>
          <div style={{ marginTop: 8 }}><TrustBullets items={listicle.trustBullets} /></div>
        </div>
      </div>
    </EditableElement>
  );
}

function CtaFreshnessBlock({ listicle, ctx }: { listicle: LpListicleContent; ctx: ListicleCtx }) {
  const c = listicle.ctaFreshness;
  const receipt = ctx.receipt;
  return (
    <EditableElement elementKey="listicle-cta-freshness" toolbarSide="left">
      <div style={{ backgroundColor: "#FFFDF8", borderRadius: 14, border: `1.5px solid ${BORDER}`, padding: 20, marginBottom: 40, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }} data-testid="listicle-cta-freshness">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "rgba(45,122,79,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Sparkles style={{ width: 14, height: 14, color: ACCENT }} />
          </div>
          <EditableText value={c.eyebrow} path={["listicle", "ctaFreshness", "eyebrow"]} as="p" className="uppercase" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: ACCENT, margin: 0 }} placeholder="Freshness guaranteed" testId="text-listicle-cta-freshness-eyebrow" />
        </div>
        {c.headline && (
          <EditableText value={c.headline} path={["listicle", "ctaFreshness", "headline"]} as="h3" style={{ fontSize: 16, fontWeight: 900, color: HEADING, lineHeight: 1.3, margin: "6px 0 10px" }} placeholder="Freshness headline" testId="text-listicle-cta-freshness-headline" />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {c.bullets.map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle2 style={{ width: 14, height: 14, color: ACCENT, flexShrink: 0 }} />
              <EditableText value={t} path={["listicle", "ctaFreshness", "bullets", i]} as="span" style={{ fontSize: 13, color: TEXT, fontWeight: 500 }} placeholder="Reassurance point" testId={`text-listicle-cta-freshness-bullet-${i}`} />
            </div>
          ))}
        </div>
        <div style={{ borderTop: `1px dashed ${BORDER}`, paddingTop: 14, marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <EditableText value={c.kitLabel} path={["listicle", "ctaFreshness", "kitLabel"]} as="span" style={{ fontSize: 13, fontWeight: 700, color: HEADING }} placeholder="Kit label" testId="text-listicle-cta-freshness-kit-label" />
            {receipt && (
              <div>
                <span style={{ fontSize: 20, fontWeight: 900, color: ACCENT }}>{receipt.priceTodayLabel}</span>
                {receipt.hasDiscount && receipt.retailLabel && (
                  <span style={{ fontSize: 11, color: "#999", marginLeft: 6, textDecoration: "line-through" }}>{receipt.retailLabel}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <CtaButton label={c.ctaText} path={["listicle", "ctaFreshness", "ctaText"]} href={ctx.offer?.checkoutUrl} testId="button-listicle-cta-freshness" />
        <div style={{ marginTop: 8 }}><TrustBullets items={listicle.trustBullets} /></div>
      </div>
    </EditableElement>
  );
}

function CtaRecordBlock({ listicle, ctx }: { listicle: LpListicleContent; ctx: ListicleCtx }) {
  const c = listicle.ctaRecord;
  return (
    <EditableElement elementKey="listicle-cta-record" toolbarSide="left">
      <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, overflow: "hidden", marginBottom: 40, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }} data-testid="listicle-cta-record">
        <div style={{ backgroundColor: DEEP, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {ctx.logoUrl && <img src={ctx.logoUrl} alt="" loading="lazy" style={{ width: 24, height: 24, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.25)", objectFit: "cover" }} />}
            <EditableText value={c.header} path={["listicle", "ctaRecord", "header"]} as="span" className="uppercase" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", color: GOLD }} placeholder="Member record" testId="text-listicle-cta-record-header" />
          </div>
          <span className="uppercase" style={{ fontSize: 8, fontWeight: 800, color: ACCENT, backgroundColor: "rgba(45,122,79,0.15)", padding: "2px 6px", borderRadius: 3, letterSpacing: "0.1em" }}>Active</span>
        </div>
        <div style={{ height: 2, background: `linear-gradient(90deg, ${GOLD}, rgba(255,255,255,0.6), ${GOLD})` }} />
        <div style={{ padding: 16 }}>
          {c.items.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < c.items.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", border: `1.5px solid ${GOLD}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CheckCircle2 style={{ width: 12, height: 12, color: GOLD }} />
              </div>
              <div>
                <EditableText value={item.title} path={["listicle", "ctaRecord", "items", i, "title"]} as="span" style={{ fontSize: 13, fontWeight: 700, color: HEADING }} placeholder="Benefit" testId={`text-listicle-cta-record-title-${i}`} />{" "}
                <EditableText value={item.sub} path={["listicle", "ctaRecord", "items", i, "sub"]} as="span" style={{ fontSize: 10, color: MUTED, marginLeft: 6 }} placeholder="detail" testId={`text-listicle-cta-record-sub-${i}`} />
              </div>
            </div>
          ))}
          <div style={{ marginTop: 14 }}>
            <CtaButton label={c.ctaText} path={["listicle", "ctaRecord", "ctaText"]} href={ctx.offer?.checkoutUrl} testId="button-listicle-cta-record" />
          </div>
          <div style={{ marginTop: 8 }}><TrustBullets items={listicle.trustBullets} /></div>
        </div>
      </div>
    </EditableElement>
  );
}

// ── Sticky CTA bar ───────────────────────────────────────────────────────────

function ListicleStickyBar({
  listicle,
  ctx,
  heroRef,
}: {
  listicle: LpListicleContent;
  ctx: ListicleCtx;
  heroRef: React.RefObject<HTMLElement | null>;
}) {
  const editor = useLpEditor();
  const [visible, setVisible] = useState(false);
  const receipt = ctx.receipt;
  // Only show the sticky bar when there is a real active offer (discount) —
  // without one there's nothing to "claim", so the bar stays hidden on both
  // the public page and in the editor.
  const hasOffer = !!receipt?.hasDiscount;

  useEffect(() => {
    if (editor || !hasOffer) return;
    const el = heroRef.current;
    if (!el) return;
    // ownerDocument-scoped observer so this works inside the mobile-preview
    // iframe too (same rule as the other LP primitives).
    const win = el.ownerDocument?.defaultView ?? window;
    const observer = new win.IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { root: null, threshold: 0, rootMargin: "-20% 0px 0px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [editor, heroRef]);

  const bar = (
    <div
      style={{
        backgroundColor: DEEP, borderTop: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 -6px 24px rgba(0,0,0,0.25)",
      }}
      data-testid="listicle-sticky-bar"
    >
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          {receipt?.hasDiscount && receipt.discountPercent != null && (
            <span className="uppercase" style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 900, fontSize: 10, letterSpacing: "0.12em", padding: "2px 7px", borderRadius: 4 }}>
              {receipt.discountPercent}% OFF
            </span>
          )}
          {receipt && <span style={{ color: "#fff", fontWeight: 900, fontSize: 15, lineHeight: 1 }}>{receipt.priceTodayLabel}</span>}
          {receipt?.hasDiscount && receipt.retailLabel && (
            <span style={{ color: "rgba(255,255,255,0.4)", textDecoration: "line-through", fontSize: 11 }}>{receipt.retailLabel}</span>
          )}
          <EditableText
            value={listicle.sticky.noteLine}
            path={["listicle", "sticky", "noteLine"]}
            as="span"
            className="hidden sm:inline"
            style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}
            placeholder="free shipping · cancel anytime"
            testId="text-listicle-sticky-note"
          />
        </div>
        {editor || !ctx.offer?.checkoutUrl ? (
          <span style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "8px 20px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap" }} data-lp-cta>
            <EditableText value={listicle.sticky.ctaText} path={["listicle", "sticky", "ctaText"]} as="span" noHideDelete placeholder="Claim the offer" testId="button-listicle-sticky-cta-label" />
          </span>
        ) : (
          <a href={ctx.offer.checkoutUrl} style={{ backgroundColor: ACCENT, color: "#fff", fontWeight: 700, padding: "8px 20px", borderRadius: 9999, fontSize: 13, whiteSpace: "nowrap", textDecoration: "none" }} data-testid="button-listicle-sticky-cta" data-lp-cta>
            {listicle.sticky.ctaText}
          </a>
        )}
      </div>
    </div>
  );

  // No active offer → no sticky bar anywhere (editor included).
  if (!hasOffer) return null;
  // Editor: show the bar inline at the end of the stack so its copy is
  // editable without it covering the canvas.
  if (editor) return <div style={{ marginTop: 12 }}>{bar}</div>;
  if (!visible) return null;
  return <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50 }}>{bar}</div>;
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function LpExpressListicle({ ctx }: { ctx: ListicleCtx }) {
  const editor = useLpEditor();
  const heroRef = useRef<HTMLElement | null>(null);
  const listicle = normalizeLpListicleContent(ctx.generatedContent?.listicle);
  const slots = useListicleSlots(ctx.generatedContent);
  const retry = ctx.onRetrySlot ? (key: string) => ctx.onRetrySlot!("listicleImages", key) : undefined;
  const receipt = ctx.receipt;
  const headingFont = ctx.typography?.heading ?? "";
  const checkoutUrl: string | undefined = ctx.offer?.checkoutUrl;

  // Visible reasons keep their ORIGINAL index for edit paths, but are numbered
  // by visible position. Interleaved blocks anchor after visible positions 1/3/5/8
  // (membership after #1, CTA blocks after #3, #5, #8 — matching the export).
  const visibleReasons = listicle.reasons
    .map((r, idx) => ({ r, idx }))
    .filter(({ r }) => (editor ? true : !r.hidden));

  const interleaveAfter = (visiblePos: number): React.ReactNode => {
    switch (visiblePos) {
      case 1: return <MembershipFramingBlock listicle={listicle} ctx={ctx} />;
      case 3: return <CtaSummaryBlock listicle={listicle} ctx={ctx} />;
      case 5: return <CtaFreshnessBlock listicle={listicle} ctx={ctx} />;
      case 8: return <CtaRecordBlock listicle={listicle} ctx={ctx} />;
      default: return null;
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: PAGE_BG }} className={ctx.typography?.body ?? ""} data-testid="listicle-page">
      {/* Announcement marquee — same look/behavior as the long-form template's
          announcement bar: deep-navy strip, cream small-caps items separated by
          alternating flower/leaf sigils in coral, slow 40s scroll (two identical
          runs so the -50% translate loops seamlessly). It scrolls in the BUILDER
          too (long-form parity); there the animation pauses on hover so the
          items stay clickable/editable, and run "a" hosts the EditableTexts. */}
      {(listicle.marqueeItems.length > 0 || editor) && (
        <div
          className="relative overflow-hidden border-b border-[var(--color-brand-coral)]/30"
          style={{ backgroundColor: DEEP, color: CREAM, width: "100%" }}
          data-testid="listicle-marquee"
        >
          <div className="lp-listicle-marquee-track py-2.5 flex whitespace-nowrap" style={{ animation: "lp-listicle-marquee 40s linear infinite" }}>
            {(["a", "b"] as const).map((runKey) => (
              <div key={runKey} className="flex items-center whitespace-nowrap shrink-0" data-testid={`listicle-marquee-run-${runKey}`}>
                {(listicle.marqueeItems.length ? listicle.marqueeItems : editor ? ["", "", ""] : []).map((t, i) => {
                  const Sigil = i % 2 === 0 ? Flower2 : Leaf;
                  return (
                    <span key={`${runKey}-${i}`} className="flex items-center">
                      {editor && runKey === "a" ? (
                        <EditableText value={t} path={["listicle", "marqueeItems", i]} as="span" className="mx-5 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] font-sans" style={{ color: CREAM }} placeholder="Marquee item" testId={`text-listicle-marquee-${i}`} />
                      ) : (
                        <span className="mx-5 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] font-sans">{t}</span>
                      )}
                      <Sigil className="w-3.5 h-3.5" style={{ color: CORAL }} aria-hidden />
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
          <style dangerouslySetInnerHTML={{ __html: `@keyframes lp-listicle-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }${editor ? ` [data-testid="listicle-marquee"]:hover .lp-listicle-marquee-track { animation-play-state: paused; }` : ""}` }} />
        </div>
      )}

      {/* Hero */}
      <section ref={heroRef} style={{ padding: "32px 16px 40px", maxWidth: 640, margin: "0 auto" }}>
        {ctx.logoUrl && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <img src={ctx.logoUrl} alt="" style={{ width: 56, height: 56, objectFit: "contain" }} data-testid="img-listicle-logo" />
          </div>
        )}
        <Eyebrow value={listicle.hero.eyebrow} path={["listicle", "hero", "eyebrow"]} testId="text-listicle-hero-eyebrow" />
        <EditableText
          value={listicle.hero.headline}
          path={["listicle", "hero", "headline"]}
          as="h1"
          className={headingFont}
          style={{ fontSize: 28, fontWeight: 900, color: HEADING, lineHeight: 1.2, textAlign: "center", marginBottom: 12, letterSpacing: "-0.01em" }}
          placeholder="Listicle headline"
          testId="text-listicle-hero-headline"
        />
        <EditableText
          value={listicle.hero.subheadline}
          path={["listicle", "hero", "subheadline"]}
          as="p"
          multiline
          style={{ fontSize: 14, lineHeight: 1.6, color: MUTED, textAlign: "center", maxWidth: 440, margin: "0 auto 20px" }}
          placeholder="Hero subheadline"
          testId="text-listicle-hero-subheadline"
        />

        {/* Hero image: AI hero slot, falling back to the real bundle photo. */}
        <div style={{ borderRadius: 14, overflow: "hidden", marginBottom: 20, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
          {slots["hero"]?.imageUrl ? (
            <ImgSlot slot={slots["hero"]} slotKey="hero" onRetry={retry} testId="img-listicle-hero" />
          ) : ctx.bundleImage ? (
            <img src={ctx.bundleImage} alt="" style={{ width: "100%", display: "block" }} data-testid="img-listicle-hero-fallback" />
          ) : (
            <div style={{ height: 240 }}>
              <ImgSlot slot={slots["hero"]} slotKey="hero" onRetry={retry} testId="img-listicle-hero" />
            </div>
          )}
        </div>

        {/* Trust row */}
        {(listicle.trustItems.length > 0 || editor) && (
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 16px", marginBottom: 20 }}>
            {ctx.reviewDisplay?.hasReviews && (
              <span style={{ fontSize: 10, fontWeight: 600, color: MUTED, display: "flex", alignItems: "center", gap: 4 }} data-testid="text-listicle-trust-reviews">
                <span style={{ color: "#F5A623" }}>★★★★★</span> {ctx.reviewDisplay.ratingText} · {ctx.reviewDisplay.reviewCountText} reviews
              </span>
            )}
            {(listicle.trustItems.length ? listicle.trustItems : ["", "", ""]).map((t, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 600, color: MUTED, display: "flex", alignItems: "center", gap: 4 }}>
                <CheckCircle2 style={{ width: 11, height: 11, color: ACCENT }} />
                <EditableText value={t} path={["listicle", "trustItems", i]} as="span" placeholder="Trust item" testId={`text-listicle-trust-item-${i}`} />
              </span>
            ))}
          </div>
        )}

        {/* Offer summary card — prices are ALWAYS derived from real data. */}
        <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }} data-testid="listicle-offer-card">
          {(listicle.offerCard.title || editor) && (
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              <EditableText value={listicle.offerCard.title} path={["listicle", "offerCard", "title"]} as="p" style={{ fontSize: 15, fontWeight: 900, color: HEADING, margin: 0 }} placeholder="Offer title" testId="text-listicle-offer-title" />
              <EditableText value={listicle.offerCard.subtitle} path={["listicle", "offerCard", "subtitle"]} as="p" style={{ fontSize: 11, color: MUTED, margin: "2px 0 0" }} placeholder="Offer subtitle" testId="text-listicle-offer-subtitle" />
            </div>
          )}
          {receipt && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <EditableText value={listicle.offerCard.retailLabel} path={["listicle", "offerCard", "retailLabel"]} as="span" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: MUTED }} placeholder="Retail value" testId="text-listicle-offer-retail-label" />
                {receipt.hasDiscount && receipt.retailLabel ? (
                  <p style={{ fontSize: 16, fontWeight: 700, color: "#999", textDecoration: "line-through", margin: "2px 0 0" }} data-testid="text-listicle-offer-retail">{receipt.retailLabel}</p>
                ) : (
                  <p style={{ fontSize: 12, color: MUTED, margin: "2px 0 0" }}>{receipt.bundleLabel}</p>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <EditableText value={listicle.offerCard.todayLabel} path={["listicle", "offerCard", "todayLabel"]} as="span" className="uppercase" style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: ACCENT }} placeholder="Today" testId="text-listicle-offer-today-label" />
                <p style={{ fontSize: 28, fontWeight: 900, color: HEADING, margin: 0, lineHeight: 1 }} data-testid="text-listicle-offer-price">{receipt.priceTodayLabel}</p>
              </div>
            </div>
          )}
          {(listicle.offerCard.badges.length > 0 || receipt?.hasDiscount) && (
            <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", justifyContent: "center" }}>
              {receipt?.hasDiscount && receipt.discountPercent != null && (
                <span style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 10, padding: "3px 8px", borderRadius: 4, letterSpacing: "0.08em" }} data-testid="badge-listicle-save">
                  SAVE {receipt.discountPercent}%
                </span>
              )}
              {listicle.offerCard.badges.map((b, i) => (
                <span key={i} style={{ backgroundColor: CREAM, color: HEADING, fontWeight: 700, fontSize: 10, padding: "3px 8px", borderRadius: 4 }}>
                  <EditableText value={b} path={["listicle", "offerCard", "badges", i]} as="span" placeholder="Badge" testId={`text-listicle-offer-badge-${i}`} />
                </span>
              ))}
            </div>
          )}
          <EditableText value={listicle.offerCard.savingsLine} path={["listicle", "offerCard", "savingsLine"]} as="p" style={{ fontSize: 12, color: MUTED, textAlign: "center", marginBottom: 14 }} placeholder="Qualitative savings/urgency line" testId="text-listicle-offer-savings-line" />
          <CtaButton label={listicle.hero.ctaText} path={["listicle", "hero", "ctaText"]} href={checkoutUrl} testId="button-listicle-hero-cta" />
          <div style={{ marginTop: 10 }}><TrustBullets items={listicle.trustBullets} /></div>
        </div>
      </section>

      {/* Page-level section reorder: every major section below the hero is a
          member of ONE container-scoped LpReorderGroup ("listicle:sections"),
          so its EditableElement toolbar gains up/down arrows. Order persists on
          generatedContent.componentOrder via the shared setComponentOrder op
          (stale-proof merge, survives publish). Marquee/hero stay pinned on
          top; footer + sticky bar stay pinned at the bottom. Conditional
          sections use conditional SPREAD so absent blocks don't leave phantom
          slots in the order. */}
      <LpReorderGroup
        groupKey="listicle:sections"
        items={[
          {
            key: "listicle-reasons-block",
            node: (
              /* toolbarSide="right" so this block-level toolbar doesn't overlap
                 the first reason's own left-corner toolbar. */
              <EditableElement elementKey="listicle-reasons-block" toolbarSide="right">
                {/* Numbered reasons + interleaved blocks */}
                <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 16px" }}>
                  {visibleReasons.map(({ r, idx }, pos) => (
                    <div key={idx}>
                      <EditableElement elementKey={`listicle-reason-${idx}`} toolbarSide="left">
                        <div style={{ marginBottom: 32, ...(editor && r.hidden ? { opacity: 0.45 } : {}) }} data-testid={`listicle-reason-${idx}`}>
                          <SectionNumber n={pos + 1} />
                          <EditableText value={r.headline} path={["listicle", "reasons", idx, "headline"]} as="h2" className={headingFont} style={{ fontSize: 22, fontWeight: 900, color: HEADING, lineHeight: 1.25, marginBottom: 12 }} placeholder={`Reason ${pos + 1} headline`} testId={`text-listicle-reason-headline-${idx}`} />
                          <EditableText value={r.body} path={["listicle", "reasons", idx, "body"]} as="p" multiline style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginBottom: 16 }} placeholder="Reason body copy" testId={`text-listicle-reason-body-${idx}`} />
                          <ReasonVisual reason={r} reasonIndex={idx} ctx={ctx} slots={slots} />
                        </div>
                      </EditableElement>
                      {interleaveAfter(pos + 1)}
                    </div>
                  ))}
                </div>
              </EditableElement>
            ),
          },
          ...((listicle.science.rows.length > 0 || editor)
            ? [{
                key: "listicle-science",
                node: (
        <EditableElement elementKey="listicle-science" toolbarSide="left">
          <section style={{ padding: "48px 16px", backgroundColor: CREAM }} data-testid="listicle-science">
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <Eyebrow value={listicle.science.eyebrow} path={["listicle", "science", "eyebrow"]} testId="text-listicle-science-eyebrow" />
                <EditableText value={listicle.science.headline} path={["listicle", "science", "headline"]} as="h2" className={headingFont} style={{ fontSize: 26, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: 0, textAlign: "center" }} placeholder="Why this actually works" testId="text-listicle-science-headline" />
                <EditableText value={listicle.science.subheadline} path={["listicle", "science", "subheadline"]} as="p" multiline style={{ fontSize: 13, lineHeight: 1.6, color: MUTED, maxWidth: 420, margin: "10px auto 0", textAlign: "center" }} placeholder="Supporting line" testId="text-listicle-science-subheadline" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {listicle.science.rows.map((ing, i) => (
                  <div key={i} style={{ backgroundColor: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, overflow: "hidden", display: "flex" }}>
                    <div style={{ width: 90, flexShrink: 0, overflow: "hidden" }}>
                      <ImgSlot slot={slots[`science-${i + 1}`]} slotKey={`science-${i + 1}`} onRetry={retry} testId={`img-listicle-science-${i}`} style={{ minHeight: 90 }} />
                    </div>
                    <div style={{ padding: "12px 14px", flex: 1 }}>
                      <EditableText value={ing.name} path={["listicle", "science", "rows", i, "name"]} as="p" style={{ fontSize: 13, fontWeight: 800, color: HEADING, margin: "0 0 4px" }} placeholder="Ingredient / component" testId={`text-listicle-science-name-${i}`} />
                      <EditableText value={ing.desc} path={["listicle", "science", "rows", i, "desc"]} as="p" multiline style={{ fontSize: 11, lineHeight: 1.55, color: MUTED, margin: 0 }} placeholder="What it does" testId={`text-listicle-science-desc-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </EditableElement>
                ),
              }]
            : []),
          {
            key: "listicle-trust-section",
            node: (
      /* Trust montage */
      <EditableElement elementKey="listicle-trust-section" toolbarSide="left">
        <section style={{ padding: "48px 16px", backgroundColor: CREAM }} data-testid="listicle-trust-section">
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <Eyebrow value={listicle.trustSection.eyebrow} path={["listicle", "trustSection", "eyebrow"]} testId="text-listicle-trust-eyebrow" />
              <EditableText value={listicle.trustSection.headline} path={["listicle", "trustSection", "headline"]} as="h2" className={headingFont} style={{ fontSize: 26, fontWeight: 900, color: HEADING, lineHeight: 1.2, marginBottom: 10, textAlign: "center" }} placeholder="Trust headline" testId="text-listicle-trust-headline" />
              <EditableText value={listicle.trustSection.body} path={["listicle", "trustSection", "body"]} as="p" multiline style={{ fontSize: 14, lineHeight: 1.6, color: MUTED, maxWidth: 400, margin: "0 auto", textAlign: "center" }} placeholder="Trust body copy" testId="text-listicle-trust-body" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 6, marginBottom: 6 }}>
              <div style={{ borderRadius: 10, overflow: "hidden", minHeight: 160 }}>
                <ImgSlot slot={slots["gallery-1"]} slotKey="gallery-1" onRetry={retry} testId="img-listicle-montage-1" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ borderRadius: 10, overflow: "hidden", flex: 1, minHeight: 77 }}>
                  <ImgSlot slot={slots["gallery-2"]} slotKey="gallery-2" onRetry={retry} testId="img-listicle-montage-2" />
                </div>
                <div style={{ borderRadius: 10, overflow: "hidden", flex: 1, minHeight: 77 }}>
                  <ImgSlot slot={slots["gallery-3"]} slotKey="gallery-3" onRetry={retry} testId="img-listicle-montage-3" />
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <div style={{ borderRadius: 10, overflow: "hidden", height: 80 }}>
                <ImgSlot slot={slots["gallery-4"]} slotKey="gallery-4" onRetry={retry} testId="img-listicle-montage-4" />
              </div>
              <div style={{ borderRadius: 10, overflow: "hidden", height: 80 }}>
                <ImgSlot slot={slots["lifestyle-1"]} slotKey="lifestyle-1" onRetry={retry} testId="img-listicle-montage-5" />
              </div>
            </div>
            {listicle.trustSection.captions.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "4px 14px", marginTop: 10 }}>
                {listicle.trustSection.captions.map((c, i) => (
                  <EditableText key={i} value={c} path={["listicle", "trustSection", "captions", i]} as="span" className="uppercase" style={{ fontSize: 9, fontWeight: 700, color: MUTED, letterSpacing: "0.08em" }} placeholder="Caption" testId={`text-listicle-trust-caption-${i}`} />
                ))}
              </div>
            )}
          </div>
        </section>
      </EditableElement>
            ),
          },
          {
            key: "listicle-membership-explainer",
            node: (
      /* Membership explainer */
      <EditableElement elementKey="listicle-membership-explainer" toolbarSide="left">
        <section style={{ padding: "48px 16px", backgroundColor: PAGE_BG }} data-testid="listicle-membership-explainer">
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Eyebrow value={listicle.membershipExplainer.eyebrow} path={["listicle", "membershipExplainer", "eyebrow"]} testId="text-listicle-mx-eyebrow" />
              <EditableText value={listicle.membershipExplainer.headline} path={["listicle", "membershipExplainer", "headline"]} as="h2" className={headingFont} style={{ fontSize: 26, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: 0, textAlign: "center" }} placeholder="Membership headline" testId="text-listicle-mx-headline" />
            </div>
            <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
              <div style={{ backgroundColor: HEADING, padding: "14px 16px", textAlign: "center" }}>
                <EditableText value={listicle.membershipExplainer.cardHeader} path={["listicle", "membershipExplainer", "cardHeader"]} as="p" className="uppercase" style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: GOLD, margin: 0, textAlign: "center" }} placeholder="Membership" testId="text-listicle-mx-card-header" />
              </div>
              <div style={{ padding: 16 }}>
                {receipt && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${BORDER}` }}>
                    <div>
                      <EditableText value={listicle.membershipExplainer.firstLabel} path={["listicle", "membershipExplainer", "firstLabel"]} as="p" className="uppercase" style={{ fontSize: 10, fontWeight: 700, color: MUTED, margin: 0, letterSpacing: "0.1em" }} placeholder="First delivery" testId="text-listicle-mx-first-label" />
                      <p style={{ fontSize: 26, fontWeight: 900, color: ACCENT, margin: "2px 0 0" }} data-testid="text-listicle-mx-price">{receipt.priceTodayLabel}</p>
                    </div>
                    {receipt.hasDiscount && receipt.retailLabel && (
                      <div style={{ textAlign: "right" }}>
                        <p className="uppercase" style={{ fontSize: 10, fontWeight: 700, color: MUTED, margin: 0, letterSpacing: "0.1em" }}>Retail Value</p>
                        <p style={{ fontSize: 18, fontWeight: 700, color: "#999", textDecoration: "line-through", margin: "2px 0 0" }}>{receipt.retailLabel}</p>
                      </div>
                    )}
                  </div>
                )}
                {listicle.membershipExplainer.renewalLabel && (
                  <EditableText value={listicle.membershipExplainer.renewalLabel} path={["listicle", "membershipExplainer", "renewalLabel"]} as="p" style={{ fontSize: 12, color: MUTED, margin: "0 0 10px" }} placeholder="Renewal terms" testId="text-listicle-mx-renewal" />
                )}
                {listicle.membershipExplainer.bullets.map((b, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "rgba(45,122,79,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <CheckCircle2 style={{ width: 12, height: 12, color: ACCENT }} />
                    </div>
                    <EditableText value={b} path={["listicle", "membershipExplainer", "bullets", i]} as="span" style={{ fontSize: 13, color: TEXT, fontWeight: 500 }} placeholder="Membership benefit" testId={`text-listicle-mx-bullet-${i}`} />
                  </div>
                ))}
                <div style={{ marginTop: 16 }}>
                  <CtaButton label={listicle.membershipExplainer.ctaText} path={["listicle", "membershipExplainer", "ctaText"]} href={checkoutUrl} testId="button-listicle-mx-cta" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </EditableElement>
            ),
          },
          ...((listicle.roadmap.phases.length > 0 || editor)
            ? [{
                key: "listicle-roadmap",
                node: (
        <EditableElement elementKey="listicle-roadmap" toolbarSide="left">
          <section style={{ padding: "48px 16px 32px", backgroundColor: PAGE_BG }} data-testid="listicle-roadmap">
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <Eyebrow value={listicle.roadmap.eyebrow} path={["listicle", "roadmap", "eyebrow"]} testId="text-listicle-roadmap-eyebrow" />
                <EditableText value={listicle.roadmap.headline} path={["listicle", "roadmap", "headline"]} as="h2" className={headingFont} style={{ fontSize: 26, fontWeight: 900, color: HEADING, lineHeight: 1.2, marginBottom: 10, textAlign: "center" }} placeholder="Roadmap headline" testId="text-listicle-roadmap-headline" />
                <EditableText value={listicle.roadmap.subheadline} path={["listicle", "roadmap", "subheadline"]} as="p" multiline style={{ fontSize: 13, lineHeight: 1.6, color: MUTED, maxWidth: 420, margin: "0 auto", textAlign: "center" }} placeholder="Honest expectations line" testId="text-listicle-roadmap-subheadline" />
              </div>
              <div style={{ position: "relative", paddingLeft: 28 }}>
                <div style={{ position: "absolute", left: 11, top: 8, bottom: 8, width: 2, backgroundColor: BORDER }} />
                {listicle.roadmap.phases.map((t, pi) => (
                  <div key={pi} style={{ position: "relative", marginBottom: 20 }}>
                    <div style={{ position: "absolute", left: -22, top: 4, width: 12, height: 12, borderRadius: "50%", backgroundColor: ACCENT, border: `2px solid ${PAGE_BG}` }} />
                    <div style={{ backgroundColor: CARD, borderRadius: 12, padding: "14px 16px", border: `1px solid ${BORDER}` }}>
                      <EditableText value={t.period} path={["listicle", "roadmap", "phases", pi, "period"]} as="p" style={{ fontSize: 13, fontWeight: 800, color: HEADING, marginBottom: 8 }} placeholder="Period" testId={`text-listicle-roadmap-period-${pi}`} />
                      {t.items.map((item, ii) => (
                        <div key={ii} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
                          <CheckCircle2 style={{ width: 12, height: 12, color: ACCENT, flexShrink: 0, marginTop: 2 }} />
                          <EditableText value={item} path={["listicle", "roadmap", "phases", pi, "items", ii]} as="span" style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }} placeholder="What most people notice" testId={`text-listicle-roadmap-item-${pi}-${ii}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </EditableElement>
                ),
              }]
            : []),
          ...((listicle.faq.items.length > 0 || editor)
            ? [{
                key: "listicle-faq",
                node: (
        <EditableElement elementKey="listicle-faq" toolbarSide="left">
          <section style={{ padding: "16px 16px 32px", backgroundColor: PAGE_BG }} data-testid="listicle-faq">
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <div style={{ textAlign: "center", marginBottom: 18 }}>
                <Eyebrow value={listicle.faq.eyebrow} path={["listicle", "faq", "eyebrow"]} testId="text-listicle-faq-eyebrow" />
                <EditableText value={listicle.faq.headline} path={["listicle", "faq", "headline"]} as="h2" className={headingFont} style={{ fontSize: 24, fontWeight: 900, color: HEADING, lineHeight: 1.2, margin: 0, textAlign: "center" }} placeholder="FAQ headline" testId="text-listicle-faq-headline" />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {listicle.faq.items.map((f, i) => (
                  <FAQItem key={i} q={f.q} a={f.a} index={i} />
                ))}
              </div>
            </div>
          </section>
        </EditableElement>
                ),
              }]
            : []),
          {
            key: "listicle-final-cta",
            node: (
      /* Final CTA */
      <EditableElement elementKey="listicle-final-cta" toolbarSide="left">
        <section style={{ padding: "32px 16px 80px", backgroundColor: PAGE_BG }} data-testid="listicle-final-cta">
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <Eyebrow value={listicle.finalCta.eyebrow} path={["listicle", "finalCta", "eyebrow"]} testId="text-listicle-final-eyebrow" />
              <EditableText value={listicle.finalCta.headline} path={["listicle", "finalCta", "headline"]} as="h2" className={headingFont} style={{ fontSize: 28, fontWeight: 900, color: HEADING, lineHeight: 1.2, marginBottom: 12, textAlign: "center" }} placeholder="Final headline" testId="text-listicle-final-headline" />
              <EditableText value={listicle.finalCta.subheadline} path={["listicle", "finalCta", "subheadline"]} as="p" multiline style={{ fontSize: 14, lineHeight: 1.6, color: MUTED, maxWidth: 400, margin: "0 auto", textAlign: "center" }} placeholder="Final supporting copy" testId="text-listicle-final-subheadline" />
            </div>
            {listicle.finalCta.reassurance.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                {listicle.finalCta.reassurance.slice(0, 2).map((ra, i) => (
                  <div key={i} style={{ backgroundColor: CARD, borderRadius: 10, padding: 12, border: `1px solid ${BORDER}` }}>
                    <EditableText value={ra.q} path={["listicle", "finalCta", "reassurance", i, "q"]} as="p" style={{ fontSize: 11, fontWeight: 800, color: HEADING, margin: "0 0 4px" }} placeholder='"What if…?"' testId={`text-listicle-final-reassure-q-${i}`} />
                    <EditableText value={ra.a} path={["listicle", "finalCta", "reassurance", i, "a"]} as="p" multiline style={{ fontSize: 11, color: MUTED, lineHeight: 1.45, margin: 0 }} placeholder="Short reassurance" testId={`text-listicle-final-reassure-a-${i}`} />
                  </div>
                ))}
              </div>
            )}
            <div style={{ backgroundColor: CARD, borderRadius: 14, border: `1.5px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
              {ctx.bundleImage && <img src={ctx.bundleImage} alt="" loading="lazy" style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover" }} data-testid="img-listicle-final-product" />}
              <div style={{ padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12 }}>
                  <div>
                    <EditableText value={listicle.finalCta.productName || receipt?.bundleLabel || ""} path={["listicle", "finalCta", "productName"]} as="p" style={{ fontSize: 16, fontWeight: 900, color: HEADING, margin: 0 }} placeholder="Product name" testId="text-listicle-final-product-name" />
                    <EditableText value={listicle.finalCta.productMeta} path={["listicle", "finalCta", "productMeta"]} as="p" style={{ fontSize: 12, color: MUTED, margin: "2px 0 0" }} placeholder="One-line meta" testId="text-listicle-final-product-meta" />
                  </div>
                  {receipt && (
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontSize: 24, fontWeight: 900, color: ACCENT, margin: 0 }} data-testid="text-listicle-final-price">{receipt.priceTodayLabel}</p>
                      {receipt.hasDiscount && receipt.retailLabel && (
                        <p style={{ fontSize: 11, color: "#999", textDecoration: "line-through", margin: 0 }}>{receipt.retailLabel}</p>
                      )}
                    </div>
                  )}
                </div>
                {(listicle.finalCta.badges.length > 0 || receipt?.hasDiscount) && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
                    {receipt?.hasDiscount && receipt.discountPercent != null && (
                      <span style={{ backgroundColor: CORAL, color: "#fff", fontWeight: 800, fontSize: 10, padding: "3px 8px", borderRadius: 4 }}>{receipt.discountPercent}% OFF</span>
                    )}
                    {listicle.finalCta.badges.map((b, i) => (
                      <span key={i} style={{ backgroundColor: CREAM, color: HEADING, fontWeight: 700, fontSize: 10, padding: "3px 8px", borderRadius: 4 }}>
                        <EditableText value={b} path={["listicle", "finalCta", "badges", i]} as="span" placeholder="Badge" testId={`text-listicle-final-badge-${i}`} />
                      </span>
                    ))}
                  </div>
                )}
                <CtaButton label={listicle.finalCta.ctaText} path={["listicle", "finalCta", "ctaText"]} href={checkoutUrl} testId="button-listicle-final-cta" />
                <EditableText value={listicle.finalCta.finePrint} path={["listicle", "finalCta", "finePrint"]} as="p" style={{ textAlign: "center", fontSize: 11, color: MUTED, marginTop: 10, fontWeight: 500 }} placeholder="Fine print" testId="text-listicle-final-fine-print" />
              </div>
            </div>
          </div>
        </section>
      </EditableElement>
            ),
          },
        ]}
      />

      {/* Footer */}
      <footer style={{ backgroundColor: DEEP, padding: "32px 16px", textAlign: "center" }} data-testid="listicle-footer">
        {ctx.logoUrl && (
          <img src={ctx.logoUrl} alt="" loading="lazy" style={{ width: 48, height: 48, margin: "0 auto 12px", display: "block", borderRadius: "50%", border: "2px solid rgba(255,255,255,0.1)", objectFit: "cover" }} />
        )}
        <p className="uppercase" style={{ fontSize: 12, fontWeight: 700, color: GOLD, letterSpacing: "0.15em", marginBottom: 4 }}>{ctx.brand?.name ?? ""}</p>
        <p style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>© {new Date().getFullYear()} {ctx.brand?.name ?? ""}. All rights reserved.</p>
      </footer>

      <ListicleStickyBar listicle={listicle} ctx={ctx} heroRef={heroRef} />
    </div>
  );
}
